import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";

// Rails
import { DirectUpload } from "@rails/activestorage";

// i18n
import { useTranslation } from "react-i18next";

// Framer Motion
import { motion } from "framer-motion";

// React Icons
import { FiUploadCloud } from "react-icons/fi";
import { FaTrash } from "react-icons/fa";

// MUI
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

// Components
import AvatarOption from "./AvatarOption";
import Spinner from "../../../../components/spinner";

// Utils
import useCsrfToken from '../../../../utils/hooks/csfrToken';
import { profileUpdateUrl } from '../../../../utils/urls';

// Types
import { User } from '../../../../types';

// Styles
import styles from "./style.module.scss";

// Assets
import option1 from "../../../../../../public/default_avatars/avatarOption1.png";
import option2 from "../../../../../../public/default_avatars/avatarOption2.png";
import option3 from "../../../../../../public/default_avatars/avatarOption3.png";
import option4 from "../../../../../../public/default_avatars/avatarOption4.png";
import option5 from "../../../../../../public/default_avatars/avatarOption5.png";
import option6 from "../../../../../../public/default_avatars/avatarOption6.png";
import option7 from "../../../../../../public/default_avatars/avatarOption7.png";
import option8 from "../../../../../../public/default_avatars/avatarOption8.png";
import option9 from "../../../../../../public/default_avatars/avatarOption9.png";
import option10 from "../../../../../../public/default_avatars/avatarOption10.png";
import Typography from "@mui/material/Typography";

interface ProfilePictureProps {
  currentAvatar: string | null;
  onUpdateUser?: (updatedUser: Partial<User>) => void;
}

// Utility for circular indexing
function mod(n: number, m: number) {
  return ((n % m) + m) % m;
}

const SIDE_COUNT = 2;

const ProfilePicture: React.FC<ProfilePictureProps> = ({ currentAvatar, onUpdateUser }) => {
  // Translations
  const { t } = useTranslation();

  // CSRF Token
  const csrfToken = useCsrfToken();

  // State
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [view, setView] = useState<'avatarGallery' | 'avatarUpload' | 'avatarUploaded'>(currentAvatar ? 'avatarUploaded' : 'avatarGallery');
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(currentAvatar ? 'current' : null);
  const [uploadedAvatar, setUploadedAvatar] = useState<string | null>(null);
  const [userUploadedAvatar, setUserUploadedAvatar] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Methods

  const updateAvatarBackend = useCallback(async (avatarData: string | File) => {
    try {
      let body: FormData | string;
      let headers: Record<string, string>;

      if (avatarData instanceof File) {
        body = new FormData();
        body.append('user[profile_picture]', avatarData);
        headers = {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        };
      } else {
        if (avatarData.startsWith('/default_avatars/')) {
          const avatarName = avatarData.split('/').pop()?.replace('.png', '');
          body = JSON.stringify({ 
            user: { 
              default_avatar: avatarName 
            } 
          });
        } else {
          body = JSON.stringify({ 
            user: { 
              profile_picture: avatarData 
            } 
          });
        }
        headers = {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        };
      }

      const response = await fetch(profileUpdateUrl(), {
        method: 'PATCH',
        headers,
        body,
      });

      if (response.ok) {
        const data = await response.json();
        if (onUpdateUser && data.user) {
          onUpdateUser({
            avatar: data.user.avatar
          });
        } else {
          console.warn('⚠️ onUpdateUser not available or no uer data in response');
        }

        return { success: true, data };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData };
      }
    } catch (error) {
      return { success: false, error };
    } finally {
      console.warn('Avatar update completed');
    }
  }, [csrfToken, onUpdateUser]);

  const uploadAvatar = async (file: File) => {
    if (!file) return;

    setIsUploading(true);
    setUploadedFile(file);

    const directUpload = new DirectUpload(file, "/rails/active_storage/direct_uploads");

    directUpload.create((error, attributes) => {
      if (error) console.error(error);
      else {
        const url = `/rails/active_storage/blobs/redirect/${attributes?.signed_id}/${encodeURIComponent(attributes?.filename || "")}`;
        setUploadedAvatar(attributes?.signed_id || null);
        setUserUploadedAvatar(url);
        setSelectedAvatar(url);
      }
      setIsUploading(false);
    });
  }

  // Memoized components
  const options = useMemo(() => {
    return [
      { id: 'avatarOption1', src: option1, alt: "Avatar Option 1" },
      { id: 'avatarOption2', src: option2, alt: "Avatar Option 2" },
      { id: 'avatarOption3', src: option3, alt: "Avatar Option 3" },
      { id: 'avatarOption4', src: option4, alt: "Avatar Option 4" },
      { id: 'avatarOption5', src: option5, alt: "Avatar Option 5" },
      { id: 'avatarOption6', src: option6, alt: "Avatar Option 6" },
      { id: 'avatarOption7', src: option7, alt: "Avatar Option 7" },
      { id: 'avatarOption8', src: option8, alt: "Avatar Option 8" },
      { id: 'avatarOption9', src: option9, alt: "Avatar Option 9" },
      { id: 'avatarOption10', src: option10, alt: "Avatar Option 10" },
    ]
  }, [userUploadedAvatar]);

  useEffect(() => {
    if (currentAvatar && !selectedAvatar) {
      setSelectedAvatar('current');
      setView('avatarUploaded');
    }
  }, [currentAvatar]);

  const handleChangeView = useCallback((newView: 'avatarGallery' | 'avatarUpload' | 'avatarUploaded') => {
    setView(newView);
    setSelectedAvatar(null);
  }, [view]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length === 0) return;

    const file = e.dataTransfer.files?.[0];
    if (file) uploadAvatar(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadAvatar(file);
  };

  // Handler to set direction and selected avatar
  const handleSelectAvatar = (id: string) => {
    setSelectedAvatar(id);
  };

  const handleUserUploadedAvatar = (avatarSrc: string | null) => {
    setUserUploadedAvatar(avatarSrc);
  };

  const handleDeleteUserUploadedAvatar = useCallback(async () => {
    try {
      const response = await fetch('/profile/avatar', {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (onUpdateUser && data.user) {
          onUpdateUser({
            avatar: data.user.avatar
          });
        }
        
        setUserUploadedAvatar(null);
        setUploadedAvatar(null);
        setSelectedAvatar(null);
        setUploadedFile(null);
        setView('avatarGallery');
      } else {
        console.error('Failed to delete avatar');
      }
    } catch (error) {
      console.error('Error deleting avatar:', error);
    }
  }, [csrfToken, onUpdateUser]);


  const handleConfirmAvatar = useCallback(async () => {
    if (uploadedFile) {
      const result = await updateAvatarBackend(uploadedFile);
      if (result.success) {
        setView('avatarUploaded')
        setUploadedFile(null);
      }
    } else if (selectedAvatar) {
      const selectedOption = options.find(opt => opt.id === selectedAvatar);
      if (selectedOption) {
        const defaultAvatarPath = `/default_avatars/${selectedOption.id}.png`;
        const result = await updateAvatarBackend(defaultAvatarPath);
        if (result.success) {
          setView('avatarUploaded')
        }
      } else {
        const result = await updateAvatarBackend(selectedAvatar);
        if (result.success) {
          setView('avatarUploaded')
        }
      }
    }
  }, [selectedAvatar, uploadedFile, options, updateAvatarBackend]);
  

  // Renderers
  const renderCenterAvatar = useCallback(() => {
    if (!selectedAvatar) return null;
    let option;
    if (currentAvatar && view === 'avatarUploaded') {
      option = { id: 'current', src: currentAvatar, alt: "Current Avatar" };
    } else {
      option = options.find((option) => option.id === selectedAvatar);
      if (!option && selectedAvatar && selectedAvatar.startsWith('/rails/active_storage/')) {
        option = { id: 'uploaded', src: selectedAvatar, alt: "Uploaded Avatar" };
      }
    }
    if (!option) return null;
    return (
      <div className="flex flex-row items-center justify-center gap-2 p-2 relative">
        <motion.div
          key={option.id}
          layout
          transition={{ type: "spring", stiffness: 500, damping: 30, duration: 1 }}
        >
          <AvatarOption
            avatarSrc={option.src}
            onUserUploadedAvatar={handleUserUploadedAvatar}
            selected
          />
        </motion.div>
      </div>
    );
  }, [selectedAvatar, options, handleDeleteUserUploadedAvatar]);

  const renderLeftAvatars = useCallback(() => {
    const selectedIndex = options.findIndex((option) => option.id === selectedAvatar);
    const leftOptions = [];
    for (let i = SIDE_COUNT; i > 0; i--) {
      const idx = mod(selectedIndex - i, options.length);
      leftOptions.push(options[idx]);
    }
    return (
      <div className="flex flex-row items-center justify-end gap-2 w-1/3 overflow-x-hidden">
        {leftOptions.map((option) => (
          <motion.div
            key={option.id}
            layout
            transition={{ type: "spring", stiffness: 500, damping: 30, duration: 1 }}
          >
            <AvatarOption
              key={option.src}
              avatarSrc={option.src}
              onSelect={() => handleSelectAvatar(option.id)}
            />
          </motion.div>
        ))}
      </div>
    )
  }, [selectedAvatar, options]);

  const renderRightAvatars = useCallback(() => {
    const selectedIndex = options.findIndex((option) => option.id === selectedAvatar);
    const rightOptions = [];
    for (let i = 1; i <= SIDE_COUNT; i++) {
      const idx = mod(selectedIndex + i, options.length);
      rightOptions.push(options[idx]);
    }
    return (
      <div className="flex flex-row items-center justify-start gap-2 w-1/3 overflow-x-hidden">
        {rightOptions.map((option) => (
          <motion.div
            key={option.id}
            layout
            transition={{ type: "spring", stiffness: 500, damping: 30, duration: 1 }}
          >
            <AvatarOption
              key={option.src}
              avatarSrc={option.src}
              onSelect={() => handleSelectAvatar(option.id)}
            />
          </motion.div>
        ))}
      </div>
    )
  }, [selectedAvatar, options]);

  const renderUserUploadedAvatar = useCallback(() => {
    const hasAvatar = userUploadedAvatar !== null;
    return (
      <div className={styles.userUploadedAvatar_container}>
        {isUploading && <Spinner />}

        {hasAvatar
          ? (
            <div className={styles.userUploadedAvatar_imageContainer}>
              <img src={userUploadedAvatar} className={styles.userUploadedAvatar_image} alt="User Uploaded Avatar" />

              <IconButton
                classes={{
                  root: styles.userUploadedAvatar_deleteIconButton,
                }}
                color='error'
                onClick={handleDeleteUserUploadedAvatar}
              >
                <FaTrash className={styles.userUploadedAvatar_deleteIcon} />
              </IconButton>
            </div>
          )
          : (
            <div
              className={`${styles.userUploadedAvatar_uploadContainer} ${isDragging ? styles.userUploadedAvatar_uploadContainer_dragging : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <FiUploadCloud className={styles.userUploadedAvatar_placeholderIcon} />

              <Typography variant='body1' color='textSecondary' className={styles.userUploadedAvatar_placeholderText}>
                <span className={styles.userUploadedAvatar_placeholderText_clickable} onClick={() => fileInputRef.current?.click()}>
                  {t('attachMaterials.upload_file')}
                </span>

                {t('attachMaterials.or_drag_and_drop')}
              </Typography>

              <input
                hidden
                type="file"
                accept="image/*"
                className={styles.userUploadedAvatar_input}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </div>
          )
        }
      </div >
    )
  }, [isDragging, isUploading, userUploadedAvatar]);

  const renderActions = useCallback(() => {
    return (
      <div className={styles.actions_container}>

        { view === 'avatarUploaded' ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleChangeView('avatarUpload')}
          >
            {t('profile.change_avatar')}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!selectedAvatar && !uploadedAvatar}
            color="primary"
            onClick={handleConfirmAvatar}>
              {t('actions.confirm')}
          </Button>
        )}
        <Button
          variant="text"
          color="primary"
          className="dark:text-white"
          onClick={() => handleChangeView( view === 'avatarGallery' ? 'avatarUpload' : 'avatarGallery')}
        >
          {view === 'avatarGallery' ? t('profile.upload_avatar') : t('profile.choose_from_gallery')}
        </Button>
      </div>
    )
  }, [selectedAvatar, view]);


  // Styles
  const rootClasses = "flex flex-col items-center justify-center gap-2 flex-1 rounded-3xl p-4 border-2 border-blue-500 bg-profile-picture-gradient dark:border-gray-500";
  return (
    <div className={`${styles.root} ${rootClasses}`}>
      <Typography
        variant='h5'
        color='textPrimary'
        className={styles.title}
      >
        {t('profile.profile_picture')}
      </Typography>

      {view === 'avatarGallery' && (
        <div className="flex flex-row items-center justify-center gap-2 w-full overflow-x-hidden">
          <>
            {renderLeftAvatars()}
            {renderCenterAvatar()}
            {renderRightAvatars()}
          </>
        </div>
      )}

      {view === 'avatarUploaded' && (
        <div className="flex flex-row items-center justify-center gap-2 w-full overflow-x-hidden">
          {renderCenterAvatar()}
        </div>
      )}

      {view === 'avatarUpload' && renderUserUploadedAvatar()}

      {renderActions()}
    </div>
  );
};

export default ProfilePicture;